import { refreshCart, updateLine } from './themeCart.js'

const SECTION_ID = 'main-cart'

class CartPage extends HTMLElement {
  #abort
  #busy = false
  // Set to true while this element is performing its own cart change so the
  // `theme:cart:updated` listener below can skip the redundant section
  // refetch (the change.js response already drove the refresh).
  #ownChange = false

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    document.addEventListener(
      'theme:cart:updated',
      () => {
        if (this.#ownChange) {
          this.#ownChange = false
          return
        }
        this.#refreshFromServer().catch((err) => console.error(err))
      },
      { signal },
    )

    this.addEventListener('click', (e) => this.#onClick(e), { signal })
    this.addEventListener('change', (e) => this.#onChange(e), { signal })

    this.addEventListener(
      'click',
      (e) => {
        const applyBtn = e.target.closest('[data-cart-promo-apply]')
        if (!applyBtn) return
        const input = this.querySelector('[data-cart-promo-input]')
        const code = input?.value?.trim()
        if (code) {
          globalThis.location.href = `/checkout?discount=${encodeURIComponent(code)}`
        }
      },
      { signal },
    )

    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-cart-promo-input]')) {
          const code = e.target.value?.trim()
          if (code) {
            globalThis.location.href = `/checkout?discount=${encodeURIComponent(code)}`
          }
        }
      },
      { signal },
    )
  }

  disconnectedCallback() {
    this.#abort?.abort()
  }

  #lineFor(target) {
    const item = target.closest('[data-cart-page-item]')
    if (!item) return null
    const line = Number.parseInt(item.dataset.line, 10)
    if (!line) return null
    return { item, line }
  }

  async #onClick(event) {
    const minus = event.target.closest('[data-cart-page-qty-minus]')
    const plus = event.target.closest('[data-cart-page-qty-plus]')
    const remove = event.target.closest('[data-cart-page-remove]')
    if (!minus && !plus && !remove) return
    if (this.#busy) return

    const ctx = this.#lineFor(event.target)
    if (!ctx) return
    const { item, line } = ctx

    const qtyEl = item.querySelector('[data-cart-page-qty-value]')
    const currentQty = Number.parseInt(qtyEl?.textContent || '1', 10)

    let newQty
    if (remove) {
      newQty = 0
    } else if (minus) {
      newQty = Math.max(0, currentQty - 1)
    } else {
      newQty = currentQty + 1
    }

    await this.#run(async () => {
      const result = await updateLine(line, { quantity: newQty }, SECTION_ID)
      await this.#refreshFromServer(result)
      await refreshCart()
    })
  }

  async #onChange(event) {
    if (this.#busy) return

    const packSelect = event.target.closest('[data-cart-page-pack-select]')
    if (packSelect) {
      await this.#handlePackChange(packSelect)
    }
  }

  async #handlePackChange(select) {
    const ctx = this.#lineFor(select)
    if (!ctx) return
    const { item, line } = ctx

    const newVariantId = Number.parseInt(select.value, 10)
    if (!newVariantId) return

    const quantity = Number.parseInt(item.dataset.lineQuantity || '1', 10) || 1
    // Preserve the line's existing subscription (chosen on the product page)
    // when swapping the pack/variant.
    const sellingPlan = item.dataset.sellingPlan && item.dataset.sellingPlan !== '0'
      ? item.dataset.sellingPlan
      : ''

    // Cart Ajax has no in-place "swap variant on a line" — remove the old
    // line and add a new one with the new variant, preserving qty + plan.
    await this.#run(async () => {
      await updateLine(line, { quantity: 0 })
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: newVariantId,
              quantity,
              ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
            },
          ],
          sections: SECTION_ID,
        }),
      })
      if (!addResp.ok) {
        const data = await addResp.json().catch(() => ({}))
        throw new Error(data.description || data.message || 'Could not change pack size')
      }
      const addData = await addResp.json().catch(() => ({}))
      await this.#refreshFromServer(addData)
      await refreshCart()
    })
  }

  async #run(fn) {
    this.#busy = true
    this.#ownChange = true
    try {
      await fn()
    } catch (err) {
      console.error(err)
      this.#ownChange = false
    } finally {
      this.#busy = false
    }
  }

  // Swaps `<cart-page>`'s inner contents with the freshly server-rendered
  // markup, either from a change.js response that included `sections`, or
  // (as a fallback) by hitting the section rendering API directly.
  async #refreshFromServer(changeResult) {
    let html = changeResult?.sections?.[SECTION_ID]
    if (!html) {
      try {
        const response = await fetch(`/cart?sections=${SECTION_ID}`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        })
        if (!response.ok) return
        const data = await response.json().catch(() => ({}))
        html = data?.[SECTION_ID]
      } catch (err) {
        console.error('Cart section refresh failed', err)
        return
      }
    }
    if (!html) return

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const fresh = doc.querySelector('cart-page')
    if (!fresh) {
      // Cart went empty — server rendered the empty state; reload to pick up
      // the simpler layout instead of trying to splice it in here.
      globalThis.location.reload()
      return
    }
    this.innerHTML = fresh.innerHTML
    // innerHTML does not update host attributes; keep recommendations URL in
    // sync with the first cart line after section re-render.
    const recUrl = fresh.getAttribute('data-recommendations-url')
    if (recUrl) {
      this.setAttribute('data-recommendations-url', recUrl)
    } else {
      this.removeAttribute('data-recommendations-url')
    }
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage)
}
